import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';

import { collection, query, where, getDocs } from 'firebase/firestore';

import { v4 } from 'uuid';
import IAddress from '~/interfaces/IAddress';
import ITable from '~/interfaces/ITable';

import { ICartProductPOS } from '../interfaces/ICartProduct';
import { useAuth } from './auth';
import { DB } from '~/services/firebase';

interface CartContextData {
  products: ICartProductPOS[];
  setProducts(value: ICartProductPOS[]): void;
  showConfirmOrder: boolean;
  setShowConfirmOrder(value: boolean): void;
  add(product: ICartProductPOS): void;
  update(product: ICartProductPOS): void;
  remove(id: string): void;
  increment(id: string): void;
  decrement(id: string): void;
  clear(): void;
  productCart: ICartProductPOS;
  setProductCart(value: ICartProductPOS): void;
  openModalCart: boolean;
  setOpenModalCart(value: boolean): void;
  showOrderAddress: boolean;
  setShowOrderAddress(value: boolean): void;
  discountPercentage: number;
  discountValue: number;
  updateDiscount(value: number, percentage: number): void;
  orderAddress: IAddress;
  setOrderAddress(value: IAddress): void;
  orderAddressText: string;
  setOrderAddressText(value: string): void;
  additionValue: number;
  setAdditionValue: (value: number) => void;
  tables: ITable[];
  setTables: (value: ITable[]) => void;
  loadTables: () => Promise<void>;
  tableSelected: ITable;
  setTableSelected: (value: ITable) => void;
}

const CartContext = createContext<CartContextData>({} as CartContextData);

export const CartCompanyProvider: React.FC = ({ children }) => {
  const { company, user } = useAuth();

  const [showConfirmOrder, setShowConfirmOrder] = useState(false);
  const [showOrderAddress, setShowOrderAddress] = useState(false);
  const [openModalCart, setOpenModalCart] = useState(false);
  const [productCart, setProductCart] = useState({} as ICartProductPOS);
  const [products, setProducts] = useState([] as ICartProductPOS[]);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [additionValue, setAdditionValue] = useState(0);
  const [orderAddress, setOrderAddress] = useState({} as IAddress);
  const [orderAddressText, setOrderAddressText] = useState('');
  const [tables, setTables] = useState<ITable[]>([]);
  const [tableSelected, setTableSelected] = useState({} as ITable);

  const updateDiscount = useCallback((value, percentage) => {
    setDiscountValue(parseFloat(value.toFixed(2)));
    setDiscountPercentage(parseFloat(percentage.toFixed(2)));
  }, []);

  const clear = useCallback(() => {
    setProducts([]);
  }, []);

  const add = useCallback((product: ICartProductPOS) => {
    setProducts((current: any) => [
      { ...product, id: v4(), product_id: product.uid },
      ...current,
    ]);

    new Audio('../assets/sound/beep.mp3').play();
  }, []);

  const update = useCallback(
    (updatedProduct: ICartProductPOS) => {
      const items = [...products];

      const findIndex = items.findIndex(item => item.id === updatedProduct.id);

      if (findIndex !== -1) {
        items[findIndex] = updatedProduct;

        setProducts(items);
      }
    },
    [products],
  );

  const remove = useCallback(
    (id: string) => {
      const items = [...products];

      const findIndex = items.findIndex(item => item.id === id);

      if (findIndex !== -1) {
        items.splice(findIndex, 1);

        setProducts(items);

        new Audio('../assets/sound/remove.mp3').play();
      }
    },
    [products, setProducts],
  );

  const increment = useCallback(
    (id: string) => {
      const items = [...products];

      const findIndex = items.findIndex(item => item.id === id);

      if (findIndex !== -1) {
        items[findIndex].quantity += 1;

        setProducts(items);
      }
    },
    [products, setProducts],
  );

  const decrement = useCallback(
    (id: string) => {
      const items = [...products];

      const findIndex = items.findIndex(item => item.id === id);

      if (findIndex !== -1) {
        if (items[findIndex].quantity === 1) remove(id);

        if (items[findIndex].quantity > 1) {
          items[findIndex].quantity -= 1;
          setProducts(items);
        }
      }
    },
    [products, setProducts, remove],
  );

  const loadTables = useCallback(async () => {
    if (!company.uid) return;

    const q = query(
      collection(DB, 'tables'),
      where('company_id', '==', company.uid),
    );
    const querySnapshot = await getDocs(q);

    const items: ITable[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as ITable;

      items.push(parseItem);
    });

    items.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

    setTables(items);
  }, [company, setTables]);

  useEffect(() => {
    if (company.app_domain) {
      const cart = localStorage.getItem(`@POS:${company.app_domain}:cart`);
      if (cart) setProducts(JSON.parse(cart));
    }
  }, [company]);

  useEffect(() => {
    if (company.app_domain) {
      localStorage.setItem(
        `@POS:${company.app_domain}:cart`,
        JSON.stringify(products),
      );
    }
  }, [products, company]);

  return (
    <CartContext.Provider
      value={{
        orderAddress,
        setOrderAddress,
        products,
        add,
        remove,
        increment,
        decrement,
        showConfirmOrder,
        setShowConfirmOrder,
        update,
        clear,
        productCart,
        setProductCart,
        openModalCart,
        setOpenModalCart,
        showOrderAddress,
        setShowOrderAddress,
        updateDiscount,
        discountPercentage,
        discountValue,
        orderAddressText,
        setOrderAddressText,
        additionValue,
        setAdditionValue,
        tables,
        setTables,
        loadTables,
        tableSelected,
        setTableSelected,
        setProducts,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export function useCartCompany(): CartContextData {
  const context = useContext(CartContext);
  return context;
}
